"use client";

import { c as _c } from "react/compiler-runtime";
import { type FC, useEffect, useMemo, useState } from "react";
import type { ApiWave } from "@/generated/models/ApiWave";
import { formatNumberWithCommas } from "@/helpers/Helpers";
import { Time } from "@/helpers/time";
import { calculateTimeLeft } from "@/helpers/waves/time.utils";
import type { ApprovalWaveCloseStatus } from "@/helpers/waves/approve-wave.helpers";
import { getApprovalWindowEndTime } from "@/helpers/waves/approve-wave.helpers";
interface WaveApprovalStatusBarProps {
  readonly approvedCount: number | null;
  readonly closeStatus: ApprovalWaveCloseStatus;
  readonly isApprovalCountError?: boolean | undefined;
  readonly isApprovalStatusError?: boolean | undefined;
  readonly retryApprovalCount?: (() => void) | null | undefined;
  readonly retryApprovalStatus?: (() => void) | null | undefined;
  readonly wave: ApiWave;
}
const formatTimeLeft = (targetTime: number, currentMillis: number): string => {
  const timeLeft = calculateTimeLeft(targetTime, currentMillis);
  if (timeLeft.days > 0) {
    return `${timeLeft.days}d ${timeLeft.hours}h left`;
  }
  if (timeLeft.hours > 0) {
    return `${timeLeft.hours}h ${timeLeft.minutes}m left`;
  }
  return `${timeLeft.minutes}m left`;
};
const getCurrentMillisForStatusRender = (_clockTick: number, _endTime: number | null, _closeStatus: ApprovalWaveCloseStatus): number => Time.currentMillis();
const approvalStatusErrorMessage = "Unable to check approval status. " + "Voting and create controls are paused until the check succeeds.";
const approvalCountErrorMessage = "Unable to load approved count.";
interface ApprovalStatusItemProps {
  readonly label: string;
  readonly value: string;
  readonly valueClassName?: string | undefined;
}
const ApprovalStatusItem: FC<ApprovalStatusItemProps> = t0 => {
  const $ = _c(8);
  const {
    label,
    value,
    valueClassName: t1
  } = t0;
  const valueClassName = t1 === undefined ? "tw-text-iron-100" : t1;
  let t2;
  if ($[0] !== label) {
    t2 = <span className="tw-text-xs tw-font-medium tw-text-iron-500">{label}</span>;
    $[0] = label;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = `tw-text-sm tw-font-semibold ${valueClassName}`;
  let t4;
  if ($[2] !== t3 || $[3] !== value) {
    t4 = <span className={t3}>{value}</span>;
    $[2] = t3;
    $[3] = value;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t2 || $[6] !== t4) {
    t5 = <div className="tw-inline-flex tw-min-w-0 tw-items-baseline tw-gap-1.5 tw-whitespace-nowrap tw-leading-5">{t2}{t4}</div>;
    $[5] = t2;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
};
export default function WaveApprovalStatusBar(t0) {
  const $ = _c(38);
  const {
    approvedCount,
    closeStatus,
    isApprovalCountError: t1,
    isApprovalStatusError: t2,
    retryApprovalCount: t3,
    retryApprovalStatus: t4,
    wave
  } = t0;
  const isApprovalCountError = t1 === undefined ? false : t1;
  const isApprovalStatusError = t2 === undefined ? false : t2;
  const retryApprovalCount = t3 === undefined ? null : t3;
  const retryApprovalStatus = t4 === undefined ? null : t4;
  const winningThreshold = wave.wave.winning_threshold;
  const maxWinners = wave.wave.max_winners;
  let t5;
  if ($[0] !== wave) {
    t5 = getApprovalWindowEndTime(wave);
    $[0] = wave;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const endTime = t5;
  const [clockTick, setClockTick] = useState(0);
  let t6;
  if ($[2] !== clockTick || $[3] !== closeStatus || $[4] !== endTime) {
    t6 = getCurrentMillisForStatusRender(clockTick, endTime, closeStatus);
    $[2] = clockTick;
    $[3] = closeStatus;
    $[4] = endTime;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const currentMillis = t6;
  const isCountOnlyError = isApprovalCountError && !isApprovalStatusError;
  let t7;
  let t8;
  if ($[6] !== closeStatus || $[7] !== endTime) {
    t7 = () => {
      if (closeStatus !== null || endTime === null) {
        return;
      }
      const intervalId = globalThis.setInterval(() => {
        setClockTick(_temp);
      }, 1000);
      return () => {
        globalThis.clearInterval(intervalId);
      };
    };
    t8 = [closeStatus, endTime];
    $[6] = closeStatus;
    $[7] = endTime;
    $[8] = t7;
    $[9] = t8;
  } else {
    t7 = $[8];
    t8 = $[9];
  }
  useEffect(t7, t8);
  let t9;
  if ($[10] !== winningThreshold) {
    t9 = typeof winningThreshold === "number" && Number.isFinite(winningThreshold) ? formatNumberWithCommas(winningThreshold) : "Not set";
    $[10] = winningThreshold;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const thresholdLabel = t9;
  let t10;
  bb0: {
    if (isApprovalStatusError || isCountOnlyError) {
      t10 = "Unavailable";
      break bb0;
    }
    if (approvedCount === null) {
      t10 = "Checking";
      break bb0;
    }
    if (typeof maxWinners === "number" && Number.isFinite(maxWinners)) {
      let t11;
      if ($[12] !== approvedCount) {
        t11 = formatNumberWithCommas(approvedCount);
        $[12] = approvedCount;
        $[13] = t11;
      } else {
        t11 = $[13];
      }
      const approved = t11;
      let t12;
      if ($[14] !== maxWinners) {
        t12 = formatNumberWithCommas(maxWinners);
        $[14] = maxWinners;
        $[15] = t12;
      } else {
        t12 = $[15];
      }
      const max = t12;
      t10 = `${approved} / ${max}`;
      break bb0;
    }
    let t11;
    if ($[16] !== approvedCount) {
      t11 = formatNumberWithCommas(approvedCount);
      $[16] = approvedCount;
      $[17] = t11;
    } else {
      t11 = $[17];
    }
    t10 = t11;
  }
  const approvedLabel = t10;
  let statusLabel;
  if (closeStatus === "max_reached") {
    statusLabel = "Max approvals reached";
  } else {
    if (closeStatus === "ended") {
      statusLabel = "Approval window ended";
    } else {
      if (isApprovalStatusError) {
        statusLabel = "Unable to check approvals";
      } else {
        if (approvedCount === null && !isCountOnlyError) {
          statusLabel = "Checking";
        } else {
          if (endTime !== null) {
            let t11;
            if ($[18] !== currentMillis || $[19] !== endTime) {
              t11 = formatTimeLeft(endTime, currentMillis);
              $[18] = currentMillis;
              $[19] = endTime;
              $[20] = t11;
            } else {
              t11 = $[20];
            }
            statusLabel = t11;
          } else {
            statusLabel = "Open";
          }
        }
      }
    }
  }
  let statusTextClassName = "tw-text-amber-200";
  if (isApprovalStatusError) {
    statusTextClassName = "tw-text-rose-200";
  } else {
    if (closeStatus === null) {
      statusTextClassName = "tw-text-iron-100";
    }
  }
  let t11;
  bb1: {
    if (isApprovalStatusError) {
      t11 = approvalStatusErrorMessage;
      break bb1;
    }
    if (isCountOnlyError) {
      t11 = approvalCountErrorMessage;
      break bb1;
    }
    t11 = null;
  }
  const errorMessage = t11;
  let t12;
  bb2: {
    if (isApprovalStatusError) {
      t12 = retryApprovalStatus;
      break bb2;
    }
    if (isCountOnlyError) {
      t12 = retryApprovalCount;
      break bb2;
    }
    t12 = null;
  }
  const retryError = t12;
  let t13;
  if ($[21] !== thresholdLabel) {
    t13 = <ApprovalStatusItem label="Threshold" value={thresholdLabel} />;
    $[21] = thresholdLabel;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== approvedLabel) {
    t14 = <ApprovalStatusItem label="Approved" value={approvedLabel} />;
    $[23] = approvedLabel;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== statusLabel || $[26] !== statusTextClassName) {
    t15 = <ApprovalStatusItem label="Status" value={statusLabel} valueClassName={statusTextClassName} />;
    $[25] = statusLabel;
    $[26] = statusTextClassName;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== t13 || $[29] !== t14 || $[30] !== t15) {
    t16 = <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-8 tw-gap-y-1">{t13}{t14}{t15}</div>;
    $[28] = t13;
    $[29] = t14;
    $[30] = t15;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== errorMessage || $[33] !== retryError) {
    t17 = errorMessage && <div className="tw-mt-3 tw-flex tw-flex-col tw-gap-2 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between"><p className="tw-mb-0 tw-text-xs tw-font-medium tw-text-iron-400">{errorMessage}</p>{retryError && <button type="button" onClick={retryError} className="tw-inline-flex tw-w-fit tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-solid tw-border-iron-600 tw-bg-iron-900 tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-iron-100 tw-transition-colors focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-iron-300 desktop-hover:hover:tw-border-iron-400 desktop-hover:hover:tw-bg-iron-800">Try again</button>}</div>;
    $[32] = errorMessage;
    $[33] = retryError;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== t16 || $[36] !== t17) {
    t18 = <div className="tw-mt-2 tw-flex-none tw-rounded-lg tw-border tw-border-solid tw-border-iron-800 tw-bg-iron-950 tw-px-3 tw-py-2 md:tw-mt-3">{t16}{t17}</div>;
    $[35] = t16;
    $[36] = t17;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  return t18;
}
function _temp(tick) {
  return tick + 1;
}